import { Component, Inject, OnDestroy, OnInit, Input } from '@angular/core';
import { first, Subject, take } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { GlobalQuery, GlobalService } from '../../../_core/state/global';
import { CollectionService } from '../../../_core/state/collection/collection.service';
import { ActionType, Collection, createCollection } from '../../../_core/state/collection/collection.model';
import { getDataSourcesListTableCollectionTemplate } from '../../../_core/state/data-source/data-sources.data';
import { DataSourceService } from '../../../_core/state/data-source/data-source.service';
import { DataSourceQuery } from '../../../_core/state/data-source/data-souce.query';
import { AdminDataSourceDialogComponent, AdminDataSourceDialogData } from './admin-data-source-dialog/admin-data-source-dialog.component';
import { PublicDataSource } from '../../../../../../api/src/data-source/data-source.entity';
import { ConfirmDialogComponent } from '../../../shared/components/confirm-dialog/confirm-dialog.component';
import { Router } from '@angular/router';
import { DOCUMENT } from '@angular/common';
import {
	DataImportDialogDataModel,
	ImportEvent
} from '../../../shared/components/data-import-dialog/data-import-dialog-data.model';
import { DataImportDialogComponent } from '../../../shared/components/data-import-dialog/data-import-dialog.component';
import { DefaultResponse, SessionQuery } from '../../../_core/state/session';
import { formatDate } from "../../../shared/utils/commonUtil";
import { ResponseShowcaseComponent } from '../admin-data-import/response-showcase/response-showcase.component';
import { Filter } from '../../../_core/state/filter/filter.model';
import { FilterType } from '../../../../../../api/src/data-source/models/filter.model';

@Component({
	selector: 'app-admin-data-sources',
	templateUrl: './admin-data-sources.component.html',
	styleUrls: ['./admin-data-sources.component.scss', './../settings.page.scss']
})
export class AdminDataSourcesComponent implements OnInit, OnDestroy {
	public dataSourcesListTableCollection: Collection;

	// When true, open add/edit inline instead of a dialog
	@Input() inlineEditor = false;
	public editing = false;
	public editingDataSource?: PublicDataSource;

	private _unsubscribe: Subject<void> = new Subject<void>();

	constructor(
		private readonly dialog: MatDialog,
		readonly globalQuery: GlobalQuery,
		private globalService: GlobalService,
		readonly dataSourceService: DataSourceService,
		readonly dataSourceQuery: DataSourceQuery,
		readonly collectionService: CollectionService,
		private readonly router: Router,
		private readonly sessionQuery: SessionQuery,
		@Inject(DOCUMENT) private document: Document
	) {
		this.globalQuery.authenticatedSettings$.pipe(take(1)).subscribe(settings => {
			// Create search filter for the collection
			const searchFilter: Filter = {
				id: 'search',
				name: 'Search',
				category: 'search',
				paramKey: 'search',  // Changed from 'query' to 'search' to match backend
				type: FilterType.Search,
				grow: true,
				growAmount: 1,
				minWidth: 200,
				prefixIcon: 'search'
			};

			// Super admins should see ALL data sources (public and private)
			// Non-super-admins should only see private data sources
			const isSuperAdmin = this.sessionQuery.isAtLeastSuperAdmin();
			const filterConfig = isSuperAdmin ? {} : { public: false };

			this.dataSourcesListTableCollection = createCollection({
				service: dataSourceService,
				query: dataSourceQuery,
				currentTemplate: getDataSourcesListTableCollectionTemplate(),
				filters: [searchFilter],
				getMethod: 'getWithSearch',  // Use custom method for search
				queryParams: {
					sort: {
						sortBy: 'slug',
						direction: 'ASC'
					},
					filter: filterConfig
				}
			});
		});
	}

	public async ngOnInit() {}

	ngOnDestroy() {
		this._unsubscribe.next();
		this._unsubscribe.complete();
	}

	onRowAction({entity, action}) {
		switch (action.type) {
			case ActionType.EDIT:
				if (this.inlineEditor) {
					this.startInlineEditor(entity);
				} else {
					this.onDataSourceDialog(entity);
				}
				break;
			case ActionType.REMOVE:
				this.onRemoveDataSource(entity);
				break;
			case ActionType.EXPORT:
				this.onExport(entity);
				break;
		}
	}

	async onRemoveDataSource(dataSource: PublicDataSource) {
		const result = await this.dialog.open(ConfirmDialogComponent, {
			data: {
				entityName: $localize `:Delete Confirm Dialog@@adminExternalDataSourceRemoveConfirmDialogEntityName:External Data Source`,
				message: $localize `:Delete Confirm Dialog@@adminExternalDataSourceRemoveConfirmDialogMessage:This action cannot be undone.`,
				remove: true
			},
			disableClose: false
		}).afterClosed().toPromise();

		if (result) {
			const deleteResult = await this.dataSourceService.delete(dataSource.slug).toPromise();

			if (deleteResult.status === 'succeeded'){
				this.refreshDataSourceList();
			}
		}
	}

	onDataSourceDialog(dataSource?: PublicDataSource) {
		const dialogSettings: AdminDataSourceDialogData = {dataSource};

		this.dialog
			.open(AdminDataSourceDialogComponent, {
				width: '640px',
				panelClass: ['background-color-alternative', 'max-width-lg'],
				disableClose: true,
				data: dialogSettings
			})
			.afterClosed()
			.pipe(first())
			.subscribe((response: any) => {
				if (response){
					this.refreshDataSourceList();
				}
			});
	}

	startInlineEditor(dataSource?: PublicDataSource) {
		this.editingDataSource = dataSource;
		this.editing = true;
	}

	onInlineEditorClosed(response?: any) {
		this.editing = false;
		this.editingDataSource = undefined;
		if (response) {
			this.refreshDataSourceList();
		}
	}

	onRowClick({entity: dataSource} : {entity: PublicDataSource}) {
		// Clicking the name opens editor (inline or dialog)
		if (this.inlineEditor) {
			this.startInlineEditor(dataSource);
			return;
		}
		this.onDataSourceDialog(dataSource);
	}

	onLinkedClick({ entity: dataSource }: { entity: PublicDataSource }) {
		// When clicking on the name column
		if (this.inlineEditor) {
			this.startInlineEditor(dataSource);
			return;
		}
		this.onDataSourceDialog(dataSource);
	}

	private refreshDataSourceList(): void {
		this.collectionService.updateQueryParams(this.dataSourcesListTableCollection.id, {
			...this.dataSourcesListTableCollection.queryParams
		});
	}

	async onExport(dataSource?: PublicDataSource) {
		return this.dataSourceService.bulkGetFromUrl(dataSource?.id).subscribe({
			next: (data: string) => {
				const date = formatDate();
				let filename = dataSource ? `${dataSource.slug}-${date}.ccedatasource` : `data-sources-${date}.ccedatasource`;

				const url = this.document.defaultView.URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: 'text/plain' }));
				const link = this.document.createElement('a');
				link.href = url;
				link.download = filename;
				link.click();
				this.document.defaultView.URL.revokeObjectURL(url);
			},
			error: () => {
				this.globalService.triggerErrorMessage(
					undefined,
					$localize `:Error Message@@collectionContainerExportEntityErrorMessageText:There was an error downloading the export.`
				);
			}
		});
	}

	onImport() {
		const dialogSettings: DataImportDialogDataModel = {
			title: $localize`:Import Data Sources Button@@adminDataSourcesImportButton:Import data sources`,
			copy: '',
			showCancelButton: true,
			showDialogCloseButton: true,
			uploadAccept: '.json, .ccedatasource',
			dropAreaTitle: $localize`:Drop Area Title@@dataImportDialogDropAreaTitle:Choose a file or drag it here to replace the uploaded file`,
			dropAreaCopy: $localize`:Drop Area Description@@dataImportDialogDropAreaCopy:File format & any restrictions here`,
			event: ImportEvent.DataSourceImport,
			onCheckFile: async (file: File)=> this.prepareImportedFile(file)
		};

		this.dialog
			.open(DataImportDialogComponent, {
				width: '640px',
				panelClass: ['background-color-alternative', 'max-width-lg'],
				data: dialogSettings
			})
			.afterClosed()
			.pipe(first())
			.subscribe((response: any) => {
				this.collectionService.updateQueryParams(this.dataSourcesListTableCollection.id, {});

				if (response && response != 'null') {
					this.openDataShowcaseModal(response);
				}
				if (!response?.length) {
					return;
				}
			});
	}

	private async prepareImportedFile(file: File): Promise<File> {
		try {
			// Super admins should see all data sources, non-super-admins only see private ones
			const isSuperAdmin = this.sessionQuery.isAtLeastSuperAdmin();
			const filter = isSuperAdmin ? {} : { public: false };
			
			const { data: { results }}: DefaultResponse<{ results: PublicDataSource[] }> =
				await this.dataSourceService.find(filter).toPromise();
			const existingSlugs = results.map(item => item.slug);

			let imported: PublicDataSource[] = JSON.parse(await file.text());
			if (!imported) {
				throw new Error('Imported file is invalid or empty.');
			}
			if (!Array.isArray(imported)) {
				imported = [imported];
			}

			const preparedImport = [];

			for (const importedItem of imported) {
				if (!existingSlugs.includes(importedItem.slug)) {
					preparedImport.push(importedItem);
					continue;
				}

				const dialogRef = this.dialog.open(ConfirmDialogComponent, {
					data: {
						title: 'Overwrite data source',
						message: `Do you want to overwrite (${importedItem.slug})?`,

						cancel: 'No',
						confirm: 'Yes'
					},
					disableClose: false
				});

				const result = await dialogRef.afterClosed().pipe(first()).toPromise();

				if (result) {
					preparedImport.push(importedItem);
				}
			}

			return new File([new Blob([JSON.stringify(preparedImport, null, 2)])], file.name, {
				type: file.type,
			});

		} catch (error) {
			console.error('Error processing imported file:', error);
			const message = $localize`:@@fileImportError:There was an issue importing your file.`;
			this.globalService.triggerErrorMessage(undefined, message);
			return null;
		}
	}

	private openDataShowcaseModal(response: any) {
		this.dialog.open(ResponseShowcaseComponent, {
			width: '480px',
			panelClass: ['background-color-alternative', 'max-width-lg'],
			data: response
		});
	}

}
